<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <component :is="embedded ? 'div' : 'v-card'" v-bind="embedded ? {} : { elevation: 6, rounded: 'xxl' }" :class="embedded ? '' : 'app-card'">
        <v-card-title v-if="!embedded">
          <v-btn class="mx-2" fab x-small color="primary" @click="$router.back()">
            <v-icon dark>mdi-arrow-left</v-icon> </v-btn
          >Create Purchase Order
        </v-card-title>
        <component :is="embedded ? 'div' : 'v-card-text'">
          <v-row>
            <v-col sm="4">
              <v-autocomplete
                v-model="form.vendor_id"
                :items="vendorItems"
                item-text="name"
                item-value="id"
                outlined
                prepend-inner-icon="mdi-domain"
                :search-input.sync="vendorSearch"
                :filter="autocompleteFilter"
                :error-messages="errors.vendor_id"
                @change="onVendorPick"
              >
                <template v-slot:label>Vendor <span style="color:#f44336;font-weight:bold;">*</span></template>
                <template v-slot:item="{ item }">
                  <v-list-item-title v-if="item.id === CREATE_VENDOR_SENTINEL" class="blue--text font-weight-medium">
                    <v-icon small color="blue" class="mr-1">mdi-plus-circle-outline</v-icon>Create
                  </v-list-item-title>
                  <v-list-item-title v-else>{{ item.name }}</v-list-item-title>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col sm="4">
              <v-menu
                v-model="poDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.po_date"
                    outlined
                    readonly
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    :error-messages="errors.po_date"
                  >
                    <template v-slot:label>PO Date <span style="color:#f44336;font-weight:bold;">*</span></template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="form.po_date"
                  @input="poDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="4">
              <v-menu
                v-model="expectedDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.expected_delivery_date"
                    outlined
                    readonly
                    label="Expected Delivery Date"
                    prepend-inner-icon="mdi-calendar-clock"
                    v-bind="attrs"
                    v-on="on"
                    :error-messages="errors.expected_delivery_date"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.expected_delivery_date"
                  @input="expectedDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="12">
              <v-textarea
                v-model="form.remarks"
                outlined
                label="Remarks"
                prepend-inner-icon="mdi-note-text"
                rows="2"
                auto-grow
                :error-messages="errors.remarks"
              ></v-textarea>
            </v-col>
          </v-row>

          <AppSectionHeader label="Add Products" />
          <v-row align="center">
            <!-- Product gets a row of its own; variant, qty and prices go on the next -->
            <v-col cols="12" class="product-picker">
              <v-autocomplete
                v-model="picker.product_id"
                :items="productItems"
                item-text="name"
                item-value="id"
                label="Product"
                outlined
                dense
                hide-details
                :search-input.sync="productSearch"
                :filter="autocompleteFilter"
                :menu-props="{ attach: '.product-picker', offsetY: true, nudgeBottom: 4, maxHeight: 300, contentClass: 'product-picker__menu' }"
                @change="onProductPick"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-title v-if="item.id === CREATE_PRODUCT_SENTINEL" class="blue--text font-weight-medium">
                    <v-icon small color="blue" class="mr-1">mdi-plus-circle-outline</v-icon>Create "{{ typedProductName }}"
                  </v-list-item-title>
                  <!-- Name alone can't tell same-named products apart: show what's in
                       stock under it, laid out in columns across the full menu width -->
                  <template v-else>
                    <v-list-item-avatar tile size="32" class="my-0 mr-3 rounded">
                      <v-img v-if="item.image_path" :src="(mediaUrl || '') + item.image_path"></v-img>
                      <v-icon v-else color="grey lighten-1">mdi-tshirt-crew-outline</v-icon>
                    </v-list-item-avatar>
                    <div class="product-option">
                      <div class="product-option__name">
                        <div class="font-weight-medium text-truncate">{{ item.name }}</div>
                        <div class="text-caption grey--text text-truncate">{{ item.sku ? `HSN ${item.sku}` : "No HSN" }}</div>
                      </div>
                      <div class="product-option__category text-body-2 grey--text text--darken-1 text-truncate">
                        {{ item.category_path || "—" }}
                      </div>
                      <div class="product-option__variants text-caption grey--text text--darken-1 text-truncate">
                        <template v-if="(item.variants || []).length">{{ variantSummary(item) }}</template>
                        <template v-else>No variants yet</template>
                      </div>
                      <div class="product-option__stock">
                        <v-chip x-small :color="productStock(item) > 0 ? 'success' : 'grey'" text-color="white">
                          Stock {{ productStock(item) }}
                        </v-chip>
                      </div>
                    </div>
                  </template>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col sm="3">
              <v-autocomplete
                v-model="picker.variant_id"
                :items="variantItemsForPicker"
                item-text="display_name"
                item-value="id"
                label="Variant"
                outlined
                dense
                hide-details
                :search-input.sync="variantSearch"
                :filter="autocompleteFilter"
                :disabled="!picker.product_id"
                @change="onVariantPick"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-title v-if="item.id === CREATE_VARIANT_SENTINEL" class="blue--text font-weight-medium">
                    <v-icon small color="blue" class="mr-1">mdi-plus-circle-outline</v-icon>Create
                  </v-list-item-title>
                  <v-list-item-title v-else>
                    {{ item.display_name }}
                    <span class="text-caption grey--text ml-1">Stock: {{ Number(item.stock) || 0 }}</span>
                  </v-list-item-title>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col sm="2">
              <v-text-field
                v-model.number="picker.quantity"
                type="number"
                min="1"
                label="Qty"
                outlined
                dense
                hide-details
              ></v-text-field>
            </v-col>
            <v-col sm="2">
              <v-text-field
                v-model.number="picker.cost_price"
                type="number"
                min="0"
                label="Cost Price"
                outlined
                dense
                hide-details
                @input="applyMarkup"
              ></v-text-field>
            </v-col>
            <v-col sm="2">
              <v-text-field
                v-model.number="picker.margin_percent"
                type="number"
                min="0"
                label="Markup %"
                outlined
                dense
                hide-details
                @input="applyMarkup"
              ></v-text-field>
            </v-col>
            <v-col sm="2">
              <v-text-field
                v-model.number="picker.selling_price"
                type="number"
                min="0"
                label="Selling Price"
                outlined
                dense
                hide-details
                @input="picker.margin_percent = ''"
              ></v-text-field>
            </v-col>
            <v-col sm="1">
              <v-btn block color="primary" @click="addToOrder">
                <v-icon small>mdi-plus</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="12" v-if="picker.variant_id" class="mt-n2">
              <span class="text-caption grey--text">
                <template v-if="pickerLastPurchasePrice !== null">Last purchase price: ₹{{ pickerLastPurchasePrice.toFixed(2) }}</template>
                <template v-else>No previous purchase for this variant — enter what you're actually paying the vendor.</template>
                <template v-if="pickerCurrentSellingPrice !== null"> · Current selling price: ₹{{ pickerCurrentSellingPrice.toFixed(2) }} (change it above to update it)</template>
              </span>
            </v-col>
          </v-row>
          <div class="text-caption error--text mt-1" v-if="errors.items">
            {{ Array.isArray(errors.items) ? errors.items[0] : errors.items }}
          </div>

          <!-- Order Items -->
          <v-card outlined class="mt-6" v-if="form.items.length">
            <v-card-title class="text-subtitle-1 font-weight-bold py-3">
              Order Items ({{ form.items.length }})
              <v-spacer></v-spacer>
              <v-text-field
                v-model="orderItemSearch"
                placeholder="Search items in this order (name, colour, SKU)"
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                hide-details
                class="order-items__search"
                style="max-width: 360px"
              ></v-text-field>
            </v-card-title>
            <div
              v-if="orderItemSearch && orderItemSearch.trim()"
              class="px-4 pb-2 text-caption grey--text"
            >
              Showing {{ filteredOrderItems.length }} of {{ form.items.length }} items
            </div>
            <v-divider></v-divider>
            <!-- Column headings -->
            <v-row align="center" class="px-4 py-2 order-items__head" no-gutters>
              <v-col class="order-items__product">Product</v-col>
              <v-col cols="auto" class="order-items__qty text-center">Qty</v-col>
              <v-col cols="auto" class="order-items__num">Cost</v-col>
              <v-col cols="auto" class="order-items__num">Selling</v-col>
              <v-col cols="auto" class="order-items__gst">GST</v-col>
              <v-col cols="auto" class="order-items__num">Total</v-col>
              <v-col cols="auto" class="order-items__actions"></v-col>
            </v-row>
            <v-divider></v-divider>
            <div v-if="!filteredOrderItems.length" class="px-4 py-6 text-center grey--text">
              No items in this order match "{{ orderItemSearch }}"
            </div>
            <div v-for="({ item, index }, rowPos) in filteredOrderItems" :key="item._key">
              <v-row align="center" class="px-4 py-2" no-gutters>
                <v-col class="order-items__product d-flex align-center">
                  <v-avatar color="deep-purple lighten-4" size="32" class="flex-shrink-0">
                    <span class="deep-purple--text font-weight-bold">{{ index + 1 }}</span>
                  </v-avatar>
                  <div class="ml-3">
                    <div class="font-weight-medium">
                      {{ item.product_name }}
                      <span v-if="item.variant_name" class="font-weight-regular grey--text"> - {{ item.variant_name }}</span>
                    </div>
                    <div class="text-caption grey--text" v-if="item.sku">{{ item.sku }}</div>
                  </div>
                </v-col>
                <v-col cols="auto" class="order-items__qty d-flex align-center justify-center">
                  <v-btn icon x-small @click="decrementQty(item)">
                    <v-icon small>mdi-minus</v-icon>
                  </v-btn>
                  <span class="mx-2">{{ item.ordered_quantity }}</span>
                  <v-btn icon x-small @click="incrementQty(item)">
                    <v-icon small>mdi-plus</v-icon>
                  </v-btn>
                </v-col>
                <v-col cols="auto" class="order-items__num mono-figure">
                  ₹{{ Number(item.purchase_price).toFixed(2) }}
                </v-col>
                <v-col cols="auto" class="order-items__num mono-figure">
                  <template v-if="item.selling_price !== null && item.selling_price !== undefined && item.selling_price !== ''">
                    ₹{{ Number(item.selling_price).toFixed(2) }}
                  </template>
                  <span v-else class="grey--text" title="Variant's current selling price is kept">&mdash;</span>
                </v-col>
                <v-col cols="auto" class="order-items__gst mono-figure">
                  ₹{{ lineGst(item).toFixed(2) }}
                  <div class="text-caption grey--text">{{ (Number(item.gst_percentage) || 0).toFixed(2) }}%</div>
                </v-col>
                <v-col cols="auto" class="order-items__num font-weight-bold primary--text mono-figure">
                  ₹{{ lineTotal(item) }}
                </v-col>
                <v-col cols="auto" class="order-items__actions">
                  <v-btn icon color="primary" title="Edit this item" @click="openEditItem(index)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon color="error" title="Remove this item" @click="removeItem(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-divider v-if="rowPos < filteredOrderItems.length - 1"></v-divider>
            </div>
            <v-divider></v-divider>
            <div class="px-4 py-3">
              <div class="d-flex justify-space-between">
                <span class="grey--text">Subtotal</span>
                <span class="mono-figure">₹{{ totals.subtotal_amount }}</span>
              </div>
              <div class="d-flex justify-space-between">
                <span class="grey--text">GST<span v-if="totals.gst_label"> ({{ totals.gst_label }})</span></span>
                <span class="mono-figure">₹{{ totals.gst_amount }}</span>
              </div>
              <v-divider class="my-1"></v-divider>
              <div class="d-flex justify-space-between font-weight-bold">
                <span>Total</span>
                <span class="primary--text mono-figure">₹{{ totals.total_amount }}</span>
              </div>
            </div>
          </v-card>

          <template v-if="editOrderId">
            <v-divider class="mt-5 mb-3"></v-divider>
            <div class="text-caption grey--text">
              Saving updates stock by the difference in quantities and re-totals this order's bill; payments already made stay as they are.
            </div>
          </template>
          <template v-else>
          <AppSectionHeader label="Payment" />
          <v-radio-group v-model="payment.type" row hide-details class="mt-0">
            <v-radio label="Full Payment" value="full"></v-radio>
            <v-radio label="Partial Payment" value="partial"></v-radio>
            <v-radio label="Pay Later (Credit / Loan)" value="loan"></v-radio>
          </v-radio-group>

          <v-row class="mt-2" v-if="payment.type !== 'loan'">
            <v-col sm="3" v-if="payment.type === 'partial'">
              <v-text-field
                v-model.number="payment.amount"
                type="number"
                min="0"
                :max="totals.total_amount"
                label="Amount Paid Now"
                outlined
                dense
                :error-messages="errors['payment.amount']"
              ></v-text-field>
            </v-col>
            <v-col sm="3" v-else>
              <v-text-field
                :value="totals.total_amount"
                label="Amount Paid Now"
                outlined
                dense
                readonly
              ></v-text-field>
            </v-col>
            <v-col sm="3">
              <v-select
                v-model="payment.payment_mode"
                :items="paymentModes"
                item-text="text"
                item-value="value"
                label="Payment Mode"
                outlined
                dense
                :error-messages="errors['payment.payment_mode']"
              ></v-select>
            </v-col>
            <v-col sm="3">
              <v-menu
                v-model="paymentDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="payment.payment_date"
                    outlined
                    dense
                    readonly
                    label="Payment Date"
                    v-bind="attrs"
                    v-on="on"
                    :error-messages="errors['payment.payment_date']"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="payment.payment_date"
                  @input="paymentDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="payment.reference_number"
                label="Reference (optional)"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row class="mt-2" v-else>
            <v-col sm="4">
              <v-menu
                v-model="dueDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="payment.due_date"
                    outlined
                    dense
                    readonly
                    label="Pay By (optional)"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="payment.due_date"
                  @input="dueDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="8" class="d-flex align-center">
              <span class="text-caption grey--text">
                Nothing paid now - a bill is still raised so this stays trackable, and it can be settled later, in full or a little at a time, from the Vendor Payments screen.
              </span>
            </v-col>
          </v-row>
          </template>

          <v-divider class="mt-5 mb-5"> </v-divider>
          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            rounded
            color="primary"
            dark
            @click="save"
          >
            {{ editOrderId ? "Update Purchase Order" : draftOrderId ? "Complete Purchase Order" : "Save Purchase Order" }}
          </v-btn>
          <v-btn
            v-if="!editOrderId"
            class="ml-3"
            rounded
            outlined
            color="primary"
            :loading="isSavingDraft"
            :disabled="isLoading || isSavingDraft"
            @click="saveDraftClick"
          >
            Save as Draft
          </v-btn>
        </component>
      </component>
    </component>

    <!-- Create Vendor / Product / Variant inline, without leaving this page.
         Vendor and Product reuse the real create pages (same fields, same
         validation) in "embedded" mode; Variant uses a small dedicated
         dialog since there's no separate variant-only create page. -->
    <AppDialog
      :show.sync="showVendorDialog"
      max-width="900"
      persistent
      icon="mdi-truck-outline"
      title="Add New Vendor"
      subtitle="Create a vendor to purchase fabric and stock from"
    >
      <VendorCreatePage v-if="showVendorDialog" embedded :initial-name="typedVendorName" @created="onVendorCreated" @close="showVendorDialog = false" />
    </AppDialog>
    <AppDialog
      :show.sync="showProductDialog"
      max-width="1000"
      persistent
      icon="mdi-package-variant"
      title="Add Product"
      subtitle="Create a new product for your catalog"
    >
      <ProductCreatePage v-if="showProductDialog" embedded :initial-name="typedProductName" @created="onProductCreated" @close="showProductDialog = false" />
    </AppDialog>
    <!-- A draft needs a vendor: offered when the dialog is closed with lines but no vendor chosen -->
    <v-dialog v-model="showDiscardDialog" max-width="420">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">Keep this as a draft?</v-card-title>
        <v-card-text>
          A draft needs a vendor. Choose a vendor to keep these items, or discard them.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="error" @click="(showDiscardDialog = false), $emit('close')">Discard</v-btn>
          <v-btn color="primary" depressed @click="showDiscardDialog = false">Choose a vendor</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit lowered the order total below what's recorded as paid to the vendor -->
    <v-dialog v-model="showOverpaidDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          <v-icon color="warning" class="mr-2">mdi-alert-outline</v-icon>
          Paid amount is more than the corrected total
        </v-card-title>
        <v-card-text>
          <div class="d-flex justify-space-between">
            <span class="grey--text">Recorded as paid to vendor</span>
            <span class="mono-figure">₹{{ overpaid.paid.toFixed(2) }}</span>
          </div>
          <div class="d-flex justify-space-between">
            <span class="grey--text">Corrected order total</span>
            <span class="mono-figure">₹{{ overpaid.total.toFixed(2) }}</span>
          </div>
          <v-divider class="my-2"></v-divider>
          <div class="d-flex justify-space-between font-weight-bold">
            <span>Extra recorded</span>
            <span class="error--text mono-figure">₹{{ (overpaid.paid - overpaid.total).toFixed(2) }}</span>
          </div>
          <p class="mt-4 mb-0">
            If the payment was entered with the same mistake, reduce it to
            <b>₹{{ overpaid.total.toFixed(2) }}</b>. The newest payments are reduced first, each
            with a note of what changed, and the bill becomes fully paid.
          </p>
          <p class="mt-2 mb-0 text-caption grey--text">
            If you really paid the vendor the higher amount, cancel and record the refund or credit separately instead.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showOverpaidDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="isLoading"
            @click="(showOverpaidDialog = false), save(true)"
          >
            Reduce payment &amp; update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit an order item: the full product edit form, laid out like Add Product,
         with this order's Qty / Cost Price filled into each variant card -->
    <AppDialog
      :show.sync="showEditItem"
      max-width="1000"
      persistent
      icon="mdi-pencil"
      title="Edit Item"
      :subtitle="editItem ? editItem.product_name : ''"
    >
      <!-- keyed per opening so it always reloads the product and the order's current lines -->
      <ProductEditPage
        v-if="editItem"
        :key="editItem.open_key"
        embedded
        :product-id="encryptIt(String(editItem.product_id))"
        :order-lines="editItem.lines"
        @saved="(entered) => onEditedProductSaved(editItem.product_id, entered)"
        @close="showEditItem = false"
      />
    </AppDialog>
    <QuickAddVariantDialog
      :show.sync="showVariantDialog"
      :product-id="picker.product_id"
      :product-name="pickerProductName"
      :initial-color="variantSearch"
      @created="onVariantCreated"
    />
  </component>
</template>
<script>
import axios from "axios";
import AppDialog from "@/components/AppDialog.vue";
import VendorCreatePage from "@/views/vendors/create.vue";
import ProductCreatePage from "@/views/products/create.vue";
import ProductEditPage from "@/views/products/id.vue";
import QuickAddVariantDialog from "./QuickAddVariantDialog.vue";
import AppSectionHeader from "@/components/AppSectionHeader.vue";

const CREATE_VENDOR_SENTINEL = "__create_vendor__";
const CREATE_PRODUCT_SENTINEL = "__create_product__";
const CREATE_VARIANT_SENTINEL = "__create_variant__";

export default {
  name: "PurchaseOrderCreatePage",
  components: { AppDialog, VendorCreatePage, ProductCreatePage, ProductEditPage, QuickAddVariantDialog, AppSectionHeader },
  props: {
    // When true, this renders inside a dialog (opened from the Purchase
    // Orders list "+" button) instead of its own routed page.
    embedded: { type: Boolean, default: false },
    // Id of a draft purchase order to finish; when set the form opens pre-filled
    // with it and Save completes that draft instead of creating a new order.
    draftOrderId: { type: [Number, String], default: null },
    // Id of an already-placed purchase order to edit. Saving updates it in
    // place: stock moves by the difference and its bill is re-totalled.
    editOrderId: { type: [Number, String], default: null },
  },
  data() {
    return {
      // Exposed on the instance (not just the module) so the "item" slot
      // templates above can compare against them.
      CREATE_VENDOR_SENTINEL,
      CREATE_PRODUCT_SENTINEL,
      CREATE_VARIANT_SENTINEL,
      form: {
        vendor_id: null,
        po_date: new Date().toISOString().substr(0, 10),
        expected_delivery_date: null,
        remarks: "",
        items: [],
      },
      picker: {
        product_id: null,
        variant_id: null,
        quantity: 1,
        cost_price: 0,
        margin_percent: "",
        baseline_selling: null,
        selling_price: 0,
      },
      payment: {
        type: "loan",
        amount: 0,
        payment_mode: null,
        payment_date: new Date().toISOString().substr(0, 10),
        reference_number: "",
        due_date: null,
      },
      paymentModes: [
        { text: "Cash", value: 0 },
        { text: "Bank Transfer", value: 1 },
        { text: "Cheque", value: 2 },
        { text: "UPI", value: 3 },
        { text: "Other", value: 4 },
      ],
      poDateMenu: false,
      expectedDateMenu: false,
      paymentDateMenu: false,
      dueDateMenu: false,
      vendors: [],
      products: [],
      vendorSearch: "",
      productSearch: "",
      // What was typed before "Create" was picked (the search box is cleared by then)
      typedVendorName: "",
      typedProductName: "",
      variantSearch: "",
      // Filters the Order Items list below (only items already on this PO)
      orderItemSearch: "",
      showVendorDialog: false,
      showProductDialog: false,
      showVariantDialog: false,
      showEditItem: false,
      editItem: null,
      showDiscardDialog: false,
      // Edit lowered the total below what's recorded as paid
      showOverpaidDialog: false,
      overpaid: { paid: 0, total: 0 },
      isSavingDraft: false,
      isLoading: false,
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
    // Order items matching the search box, each paired with its real index in
    // form.items so numbering, edit and remove still act on the right row.
    filteredOrderItems() {
      const rows = this.form.items.map((item, index) => ({ item, index }));
      const q = (this.orderItemSearch || "").trim().toLowerCase();
      if (!q) return rows;
      return rows.filter(({ item }) =>
        [item.product_name, item.variant_name, item.sku]
          .filter((v) => v !== null && v !== undefined)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    },
    // Injects a synthetic "Create" option when the typed text doesn't match
    // anything already loaded, so the user can jump to the full create page.
    vendorItems() {
      return this.withCreateOption(
        this.vendors,
        this.vendorSearch,
        CREATE_VENDOR_SENTINEL
      );
    },
    productItems() {
      // Product names don't have to be unique, so "Create" stays on offer
      // even when the typed name matches an existing product exactly.
      return this.withCreateOption(
        this.products,
        this.productSearch,
        CREATE_PRODUCT_SENTINEL,
        "name",
        true
      );
    },
    variantsForPicker() {
      const product = this.products.find((p) => p.id === this.picker.product_id);
      return product ? product.variants || [] : [];
    },
    variantItemsForPicker() {
      if (!this.picker.product_id) {
        return [];
      }
      return this.withCreateOption(
        this.variantsForPicker,
        this.variantSearch,
        CREATE_VARIANT_SENTINEL,
        "display_name"
      );
    },
    pickerProductName() {
      const product = this.products.find((p) => p.id === this.picker.product_id);
      return product ? product.name : "";
    },
    pickerLastPurchasePrice() {
      const variant = this.variantsForPicker.find((v) => v.id === this.picker.variant_id);
      return variant && variant.last_purchase_price !== null && variant.last_purchase_price !== undefined
        ? Number(variant.last_purchase_price)
        : null;
    },
    pickerCurrentSellingPrice() {
      const variant = this.variantsForPicker.find((v) => v.id === this.picker.variant_id);
      const current = variant ? (variant.selling_price ?? variant.price) : null;
      return current !== null && current !== undefined ? Number(current) : null;
    },
    totals() {
      let subtotal = 0;
      let gst = 0;
      const rates = new Set();
      this.form.items.forEach((item) => {
        const lineSubtotal =
          (Number(item.ordered_quantity) || 0) *
          (Number(item.purchase_price) || 0);
        const lineGst = this.roundGst(lineSubtotal * ((Number(item.gst_percentage) || 0) / 100));
        subtotal += lineSubtotal;
        gst += lineGst;
        rates.add(Number(item.gst_percentage) || 0);
      });
      let gstLabel = "";
      if (rates.size === 1) {
        gstLabel = `${[...rates][0].toFixed(2)}%`;
      } else if (rates.size > 1) {
        gstLabel = "Multiple Rates";
      }
      return {
        subtotal_amount: subtotal.toFixed(2),
        gst_amount: gst.toFixed(2),
        total_amount: (subtotal + gst).toFixed(2),
        gst_label: gstLabel,
      };
    },
  },
  watch: {
    vendorSearch(v) {
      if (v && v.trim() && v !== "Create") this.typedVendorName = v.trim();
    },
    productSearch(v) {
      if (v && v.trim() && v !== "Create") this.typedProductName = v.trim();
    },
  },
  mounted() {
    this.$store.dispatch("errors/setErrors", {});
    this.getMasters();
    if (this.draftOrderId || this.editOrderId) {
      this.loadDraft();
    }
  },
  methods: {
    async getMasters() {
      await axios
        .get(`masters?context=purchase_order`)
        .then((response) => {
          this.vendors = response.data.vendors || [];
          this.products = response.data.products_with_all_variants || [];
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    // Appends a synthetic "Create" entry to a list when the typed search
    // text doesn't match anything already in it - shared by the Vendor,
    // Product and Variant pickers. Rendered in blue via each autocomplete's
    // "item" slot; picking it opens the real create page in a new tab
    // rather than creating anything inline, so nothing here is lost if the
    // rest of this PO form is still in progress.
    withCreateOption(list, search, sentinelId, textKey = "name", allowDuplicate = false) {
      if (!search) {
        return list;
      }
      const query = search.trim().toLowerCase();
      if (!query) {
        return list;
      }
      const hasExactMatch = list.some(
        (item) => (item[textKey] || "").trim().toLowerCase() === query
      );
      if (hasExactMatch && !allowDuplicate) {
        return list;
      }
      return [...list, { id: sentinelId, [textKey]: "Create" }];
    },
    productStock(product) {
      return (product.variants || []).reduce((sum, v) => sum + (Number(v.stock) || 0), 0);
    },
    // "RED - XS (96), BLUE - S (100)" - colours/sizes with what's in stock of each
    variantSummary(product) {
      const variants = product.variants || [];
      const shown = variants.slice(0, 4).map((v) => `${v.display_name || "—"} (${Number(v.stock) || 0})`);
      const more = variants.length > 4 ? ` +${variants.length - 4} more` : "";
      return shown.join(", ") + more;
    },
    // Vuetify's v-autocomplete filters `items` against the typed search
    // text itself (matching item-text as a substring) on top of whatever
    // list we already pass in. That breaks the synthetic "Create" row -
    // its label is the literal word "Create", so e.g. typing "am" (no "a"
    // and "m" together in "Create") would filter it back out even though
    // our own withCreateOption() logic decided it should still be shown.
    // Always keep sentinel rows, and fall back to the normal substring
    // match for everything else.
    autocompleteFilter(item, queryText, itemText) {
      if (
        item.id === CREATE_VENDOR_SENTINEL ||
        item.id === CREATE_PRODUCT_SENTINEL ||
        item.id === CREATE_VARIANT_SENTINEL
      ) {
        return true;
      }
      return (itemText || "").toLowerCase().indexOf((queryText || "").toLowerCase()) > -1;
    },
    // Resets a picker field back to empty on the next tick, after Vuetify
    // has finished processing the click that selected the sentinel item.
    resetField(setter) {
      this.$nextTick(setter);
    },
    onVendorPick(vendorId) {
      if (vendorId === CREATE_VENDOR_SENTINEL) {
        this.showVendorDialog = true;
        this.resetField(() => {
          this.form.vendor_id = null;
        });
      }
    },
    onVendorCreated(vendor) {
      this.vendors.push(vendor);
      this.form.vendor_id = vendor.id;
      this.vendorSearch = "";
      this.typedVendorName = "";
      this.showVendorDialog = false;
    },
    onProductPick(productId) {
      if (productId === CREATE_PRODUCT_SENTINEL) {
        this.showProductDialog = true;
        this.resetField(() => {
          this.picker.product_id = null;
        });
        return;
      }
      this.picker.variant_id = null;
      this.picker.cost_price = 0;
      this.picker.margin_percent = "";
      this.picker.baseline_selling = null;
      this.picker.selling_price = 0;
    },
    // Pushes one variant straight into the order items list, bypassing the
    // picker entirely - used both for the seeded-from-product-create flow
    // below and could be reused anywhere else a variant + qty is already known.
    pushOrderItem({ productId, productName, sku, gstPercentage, variantId, variantName, quantity, costPrice, sellingPrice }) {
      const existing = this.form.items.find((i) => i.variant_id === variantId);
      if (existing) {
        existing.ordered_quantity += quantity;
        return;
      }
      this.form.items.push({
        _key: `${productId}_${variantId}`,
        product_id: productId,
        variant_id: variantId,
        product_name: productName,
        variant_name: variantName,
        sku,
        gst_percentage: gstPercentage,
        ordered_quantity: quantity,
        purchase_price: costPrice,
        selling_price: sellingPrice,
      });
    },
    async onProductCreated(product) {
      // The create response doesn't carry the freshly-inserted variants, so
      // refetch masters to get everything back in the same consistent shape
      // the picker already renders from.
      this.showProductDialog = false;
      this.productSearch = "";
      this.typedProductName = "";
      const seedItems = Array.isArray(product.seed_items) ? product.seed_items : [];
      await this.getMasters();
      this.picker.product_id = product.id;

      if (seedItems.length) {
        // Every variant the seller gave a Qty to on the create-product form
        // goes straight into this order - no need to re-search and re-add
        // each one through the picker (this is what makes "create a product
        // with 20 variants mid-purchase" not a 20x-repeat chore).
        seedItems.forEach((seed) => {
          this.pushOrderItem({
            productId: product.id,
            productName: product.name,
            sku: product.hsn_code || "",
            gstPercentage: product.gst_value || 0,
            variantId: seed.variant_id,
            variantName: seed.display_name,
            quantity: seed.quantity,
            costPrice: seed.cost_price,
            sellingPrice: seed.selling_price,
          });
        });
        this.picker.variant_id = null;
        this.picker.cost_price = 0;
        this.picker.margin_percent = "";
        this.picker.baseline_selling = null;
        this.picker.selling_price = 0;
        return;
      }

      const refreshed = this.products.find((p) => p.id === product.id);
      const firstVariant = refreshed && refreshed.variants && refreshed.variants[0];
      if (firstVariant) {
        this.picker.variant_id = firstVariant.id;
        // A brand-new variant has no purchase history yet - default to 0
        // rather than the selling price just entered for it, so the actual
        // cost still has to be typed in deliberately.
        this.picker.cost_price = firstVariant.last_purchase_price ?? 0;
        this.picker.margin_percent = "";
        this.picker.selling_price = firstVariant.selling_price ?? firstVariant.price ?? 0;
        this.picker.baseline_selling = this.picker.selling_price;
      }
    },
    onVariantPick(variantId) {
      if (variantId === CREATE_VARIANT_SENTINEL) {
        this.showVariantDialog = true;
        this.resetField(() => {
          this.picker.variant_id = null;
        });
        return;
      }
      const variant = this.variantsForPicker.find((v) => v.id === variantId);
      // Default cost from what was actually paid last time (if known) - never
      // from the variant's selling price, which would otherwise silently
      // become the recorded cost on every purchase. Selling price defaults
      // to what the variant currently sells for (discount applied) and is
      // remembered as the baseline, so addToOrder only sends a selling price
      // when it was deliberately changed - sending it back unchanged would
      // make the backend clear the variant's discount.
      this.picker.cost_price = variant && variant.last_purchase_price !== null && variant.last_purchase_price !== undefined
        ? Number(variant.last_purchase_price)
        : 0;
      this.picker.margin_percent = "";
      this.picker.selling_price = variant ? Number(variant.selling_price ?? variant.price) || 0 : 0;
      this.picker.baseline_selling = this.picker.selling_price;
    },
    async onVariantCreated(variant) {
      this.variantSearch = "";
      await this.getMasters();
      this.picker.variant_id = variant.id;
      this.picker.cost_price = variant.last_purchase_price ?? 0;
      this.picker.margin_percent = "";
      this.picker.selling_price = variant.selling_price ?? variant.price ?? 0;
      this.picker.baseline_selling = this.picker.selling_price;
    },
    // Selling Price = Cost Price + Markup % of Cost Price (200 at 50% -> 300).
    // Does nothing until both are filled, so a hand-typed price is never
    // overwritten; typing in Selling Price clears the markup instead.
    applyMarkup() {
      const cost = Number(this.picker.cost_price);
      const pct = this.picker.margin_percent;
      if (!(cost > 0) || pct === "" || pct === null || isNaN(Number(pct))) return;
      this.picker.selling_price = Math.round(cost * (1 + Number(pct) / 100) * 100) / 100;
    },
    addToOrder() {
      const product = this.products.find((p) => p.id === this.picker.product_id);
      const variant = this.variantsForPicker.find((v) => v.id === this.picker.variant_id);
      if (!product || !variant) {
        return;
      }

      this.pushOrderItem({
        productId: product.id,
        productName: product.name,
        sku: product.sku,
        gstPercentage: product.gst_percentage,
        variantId: variant.id,
        variantName: variant.display_name,
        quantity: Number(this.picker.quantity) || 1,
        costPrice: Number(this.picker.cost_price) || 0,
        // null = leave the variant's price (and discount) untouched
        sellingPrice: Number(this.picker.selling_price) === Number(this.picker.baseline_selling)
          ? null
          : Number(this.picker.selling_price) || 0,
      });

      this.picker = { product_id: null, variant_id: null, quantity: 1, cost_price: 0, margin_percent: "", baseline_selling: null, selling_price: 0 };
    },
    // ── Edit one order line ──
    openEditItem(index) {
      const item = this.form.items[index];
      this.editItem = {
        product_id: item.product_id,
        product_name: item.product_name,
        // A fresh key per opening so the form reloads with the order as it is now
        open_key: `${item.product_id}_${Date.now()}`,
        // This order's lines for the product - each variant card is filled from them
        lines: this.form.items
          .filter((i) => i.product_id === item.product_id)
          .map((i) => ({
            variant_id: i.variant_id,
            ordered_quantity: i.ordered_quantity,
            purchase_price: i.purchase_price,
            selling_price: i.selling_price,
          })),
      };
      this.showEditItem = true;
    },
    // The product was saved from Edit Item: bring this order in line with
    // the Qty / Cost entered on each variant card (Qty 0 = off the order, as
    // on Add Product), and refresh names since a colour/size may have changed.
    async onEditedProductSaved(productId, entered) {
      await this.getMasters();
      const product = this.products.find((p) => p.id === productId);
      const variants = (product && product.variants) || [];
      const sameText = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();

      (entered || []).forEach((row) => {
        // Variants added in the dialog have no id yet - find them by colour + size
        const variant = row.variant_id
          ? variants.find((v) => v.id === row.variant_id)
          : variants.find((v) => sameText(v.color, row.color) && sameText(v.size, row.size));
        if (!variant) return;
        const index = this.form.items.findIndex((i) => i.variant_id === variant.id);
        if (row.quantity > 0) {
          if (index === -1) {
            this.pushOrderItem({
              productId,
              productName: product.name,
              sku: product.sku,
              gstPercentage: product.gst_percentage,
              variantId: variant.id,
              variantName: variant.display_name,
              quantity: row.quantity,
              costPrice: row.cost_price,
              sellingPrice: null,
            });
          } else {
            const item = this.form.items[index];
            item.ordered_quantity = row.quantity;
            item.purchase_price = row.cost_price;
            // The product save already set the catalog price - nothing left to apply
            item.selling_price = null;
          }
        } else if (index !== -1) {
          this.form.items.splice(index, 1);
        }
      });

      const removed = [];
      this.form.items.forEach((item) => {
        if (item.product_id !== productId) return;
        const variant = variants.find((v) => v.id === item.variant_id);
        if (!variant) {
          removed.push(`${item.product_name}${item.variant_name ? " - " + item.variant_name : ""}`);
          return;
        }
        item.product_name = product.name;
        item.sku = product.sku;
        item.variant_name = variant.display_name;
        // A GST rate changed in the product dialog applies to this order's lines too
        item.gst_percentage = product.gst_percentage;
      });
      if (removed.length && this.$toast) {
        this.$toast.error(`No longer in the catalog, remove from this order: ${removed.join(", ")}`);
      }
    },
    lineGst(item) {
      const subtotal = (Number(item.ordered_quantity) || 0) * (Number(item.purchase_price) || 0);
      return this.roundGst(subtotal * ((Number(item.gst_percentage) || 0) / 100));
    },
    incrementQty(item) {
      item.ordered_quantity += 1;
    },
    decrementQty(item) {
      if (item.ordered_quantity > 1) {
        item.ordered_quantity -= 1;
      }
    },
    removeItem(index) {
      this.form.items.splice(index, 1);
    },
    lineTotal(item) {
      const subtotal =
        (Number(item.ordered_quantity) || 0) * (Number(item.purchase_price) || 0);
      return (subtotal + this.lineGst(item)).toFixed(2);
    },
    // GST rounds to the nearest rupee, tying DOWN at exactly X.50 (unlike
    // standard rounding) - e.g. 99.50 -> 99, 99.51 -> 100, 99.99 -> 100.
    roundGst(value) {
      return Math.ceil(value - 0.5);
    },
    buildPaymentPayload() {
      const payload = {
        type: this.payment.type,
        reference_number: this.payment.reference_number || null,
      };
      if (this.payment.type === "loan") {
        payload.due_date = this.payment.due_date || null;
        return payload;
      }
      payload.payment_date = this.payment.payment_date;
      payload.payment_mode = this.payment.payment_mode;
      if (this.payment.type === "partial") {
        payload.amount = Number(this.payment.amount) || 0;
      }
      return payload;
    },
    // The order as the API wants it (without the payment section)
    buildOrderPayload() {
      return {
        ...this.form,
        items: this.form.items.map((item) => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          ordered_quantity: item.ordered_quantity,
          purchase_price: item.purchase_price,
          // On an edit, a selling price that wasn't touched isn't re-sent -
          // re-applying it would undo any price/discount set on the catalog since.
          selling_price: this.editOrderId && item.selling_price === item._orig_selling ? null : item.selling_price,
          gst_percentage: item.gst_percentage,
        })),
      };
    },
    // Place the order for real. Completing a draft updates it in place rather
    // than creating a second purchase order.
    async save(adjustPayments = false) {
      try {
        this.isLoading = true;
        this.$store.dispatch("errors/setErrors", {});
        if (this.editOrderId) {
          // The payment was taken when the order was placed; an edit leaves it
          // alone - unless the user confirmed trimming it to a lower new total.
          const payload = this.buildOrderPayload();
          if (adjustPayments === true) payload.adjust_payments = true;
          await axios.patch(`/purchase_orders/${this.encryptIt(String(this.editOrderId))}`, payload);
        } else if (this.draftOrderId) {
          const payload = { ...this.buildOrderPayload(), payment: this.buildPaymentPayload() };
          await axios.patch(`/purchase_orders/${this.encryptIt(this.draftOrderId)}`, payload);
        } else {
          const payload = { ...this.buildOrderPayload(), payment: this.buildPaymentPayload() };
          await axios.post("/purchase_orders", payload);
        }
        this.isLoading = false;
        if (this.embedded) {
          this.$emit("saved");
          this.$emit("close");
        } else {
          this.$router.push("/purchase-orders");
        }
      } catch (e) {
        console.log(e);
        this.isLoading = false;
        const data = e.response && e.response.data;
        if (data && data.code === "paid_exceeds_total") {
          // The corrected total is below what's recorded as paid - offer to
          // bring the recorded payment down instead of just refusing.
          this.overpaid = { paid: Number(data.paid_amount) || 0, total: Number(data.total_amount) || 0 };
          this.showOverpaidDialog = true;
        } else if (e.response && e.response.data && e.response.data.errors) {
          this.$store.dispatch("errors/setErrors", e.response.data.errors);
        } else if (e.response && e.response.data && e.response.data.message && this.$toast) {
          // e.g. an edit refused because stock was already sold
          this.$toast.error(e.response.data.message);
        }
      }
    },

    // ── Drafts ──────────────────────────────────────────────────────────────
    // A draft keeps the vendor, dates and lines but does not touch stock,
    // prices or bills - that only happens when it is completed with Save.
    async saveDraft() {
      this.isSavingDraft = true;
      try {
        this.$store.dispatch("errors/setErrors", {});
        const payload = { ...this.buildOrderPayload(), is_draft: true };
        if (this.draftOrderId) {
          await axios.patch(`/purchase_orders/${this.encryptIt(this.draftOrderId)}`, payload);
        } else {
          await axios.post("/purchase_orders", payload);
        }
        return true;
      } catch (e) {
        console.log(e);
        if (e.response && e.response.data && e.response.data.errors) {
          this.$store.dispatch("errors/setErrors", e.response.data.errors);
        }
        if (this.$toast) this.$toast.error("Could not save the draft. Please check the highlighted fields.");
        return false;
      } finally {
        this.isSavingDraft = false;
      }
    },
    // The "Save as Draft" button
    async saveDraftClick() {
      if (!this.form.vendor_id) {
        this.$store.dispatch("errors/setErrors", { vendor_id: ["Choose a vendor to save this as a draft."] });
        return;
      }
      if (!this.form.items.length) {
        this.$store.dispatch("errors/setErrors", { items: ["Add at least one product to save a draft."] });
        return;
      }
      if (await this.saveDraft()) {
        this.$emit("draft-saved");
        this.$emit("close");
      }
    },
    // Called by the dialog whenever the user tries to close it (X, Esc or a
    // click outside). Unfinished work is kept as a draft instead of being lost.
    // Resolves to { close, savedDraft } - the parent closes the dialog when
    // `close` is true.
    async requestClose() {
      // A placed order can't go back to being a draft - closing just discards the edit
      if (this.editOrderId || !this.form.items.length) {
        return { close: true, savedDraft: false };
      }
      if (!this.form.vendor_id) {
        // a draft needs a vendor - let the user choose rather than silently losing the lines
        this.showDiscardDialog = true;
        return { close: false, savedDraft: false };
      }
      const ok = await this.saveDraft();
      return { close: ok, savedDraft: ok };
    },
    async loadDraft() {
      this.isLoading = true;
      try {
        const response = await axios.get(`/purchase_orders/${this.encryptIt(String(this.editOrderId || this.draftOrderId))}`);
        const o = response.data.data;
        this.form.vendor_id = o.vendor_id;
        this.form.po_date = (o.po_date || "").substr(0, 10) || this.form.po_date;
        this.form.expected_delivery_date = o.expected_delivery_date ? o.expected_delivery_date.substr(0, 10) : null;
        this.form.remarks = o.remarks || "";
        this.orderItemSearch = "";
        this.form.items = (o.items || []).map((i) => ({
          _key: `${i.product_id}_${i.variant_id}`,
          product_id: i.product_id,
          variant_id: i.variant_id,
          product_name: i.product ? i.product.name : "",
          variant_name: i.product_variant || i.productVariant
            ? this.variantLabel(i.product_variant || i.productVariant)
            : "",
          sku: i.product ? i.product.hsn_code : "",
          gst_percentage: i.gst_percentage,
          ordered_quantity: i.ordered_quantity,
          purchase_price: Number(i.purchase_price),
          selling_price: i.selling_price === null || i.selling_price === undefined ? null : Number(i.selling_price),
        }));
        this.form.items.forEach((item) => {
          item._orig_selling = item.selling_price;
        });
      } catch (e) {
        console.log(e);
        if (this.$toast) this.$toast.error(this.editOrderId ? "Could not open this purchase order." : "Could not open this draft.");
        this.$emit("close");
      } finally {
        this.isLoading = false;
      }
    },
    variantLabel(v) {
      return `${v.color || ""}${v.size ? " - " + v.size : ""}`.trim();
    },
  },
};
</script>

<style scoped>
/* Product picker option: name | category | colours & sizes | stock, one line each,
   so the full-width menu is filled instead of everything stacked on the left */
.product-option {
  display: grid;
  grid-template-columns: minmax(140px, 1.2fr) minmax(140px, 1.3fr) minmax(160px, 2fr) 90px;
  align-items: center;
  column-gap: 16px;
  flex: 1 1 auto;
  min-width: 0;
  padding: 4px 0;
}
.product-option > div {
  min-width: 0;
}
/* Menu lives inside the field's column (attach), so it opens right under the
   field, matches its width and scrolls with the form instead of off-screen */
.product-picker {
  position: relative;
}
.product-picker ::v-deep .product-picker__menu {
  left: 12px !important;
  right: 12px;
  min-width: 0 !important;
  max-width: none !important;
  width: calc(100% - 24px);
}
.product-picker ::v-deep .product-picker__menu .v-list-item {
  min-height: 44px;
}
.product-option__stock {
  text-align: right;
}

/* Order Items: fixed-width figure columns so every row lines up under its heading */
.order-items__head {
  font-size: 12px;
  font-weight: 600;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.order-items__product {
  min-width: 180px;
}
.order-items__qty {
  width: 110px;
}
.order-items__num {
  width: 105px;
  text-align: right;
}
.order-items__gst {
  width: 95px;
  text-align: right;
}
.order-items__actions {
  width: 96px;
  text-align: right;
}
</style>
